import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { compressImage } from "../lib/imageCompression";
import { FileText, Image as ImageIcon, Paperclip, Upload, Trash2, Loader2, Download } from "lucide-react";
import { useConfirm } from "./ConfirmProvider";

/**
 * Multi-file attachments for a Variation Order.
 * Used by both ID (ProjectDetail VO tab) and homeowner (CustomerProject VO card).
 *
 * Backend endpoints:
 *   POST   /vos/{id}/documents      (multipart: file, kind, label)
 *   GET    /vos/{id}/documents/{doc_id}
 *   DELETE /vos/{id}/documents/{doc_id}
 */
const KIND_META = {
  quotation: { label: "Quotation", icon: FileText, tint: "bg-[#FCE7F3] text-[#9D174D]" },
  photo: { label: "Photo", icon: ImageIcon, tint: "bg-[#E0E7FF] text-[#3730A3]" },
  attachment: { label: "Attachment", icon: Paperclip, tint: "bg-[#F7F4EC] text-[#7A7571]" },
};

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function VOAttachments({ voId, documents = [], canManage = false, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pickerKind, setPickerKind] = useState("attachment");
  const confirm = useConfirm();

  const startUpload = (kind) => {
    setPickerKind(kind);
    inputRef.current && inputRef.current.click();
  };

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      // Shrink phone photos before send. Non-image files (PDFs, quotations)
      // pass through untouched via compressImage's early return.
      const shrunk = await compressImage(file);
      const fd = new FormData();
      fd.append("file", shrunk);
      fd.append("kind", pickerKind);
      fd.append("label", file.name);
      await api.post(`/vos/${voId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${KIND_META[pickerKind]?.label || "File"} uploaded`);
      onChange && (await onChange());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (doc) => {
    const ok = await confirm({
      title: `Remove "${doc.label}"?`,
      message: "This attachment will be permanently deleted.",
      confirmText: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/vos/${voId}/documents/${doc.doc_id}`);
      toast.success("Removed");
      onChange && (await onChange());
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="mt-3 rounded-2xl border border-[#EBE5D9] bg-[#FBF7EF]/60 p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">
          Attachments
          {documents.length > 0 && <span className="ml-2 text-[#2C2A29]">({documents.length})</span>}
        </div>
        {canManage && (
          <div className="flex items-center gap-1 flex-wrap">
            <UploadButton onClick={() => startUpload("quotation")} label="Quotation" disabled={uploading} testid={`vo-upload-quotation-${voId}`} />
            <UploadButton onClick={() => startUpload("photo")} label="Photo" disabled={uploading} testid={`vo-upload-photo-${voId}`} />
            <UploadButton onClick={() => startUpload("attachment")} label="Doc" disabled={uploading} testid={`vo-upload-attachment-${voId}`} />
          </div>
        )}
      </div>

      {documents.length === 0 ? (
        <div className="text-xs text-[#7A7571] italic py-1">
          {uploading
            ? "Uploading…"
            : canManage
              ? "Attach the quotation, supporting photos, or any related file."
              : "No attachments yet."}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {documents.map((d) => {
            const meta = KIND_META[d.kind] || KIND_META.attachment;
            const Icon = meta.icon;
            return (
              <li
                key={d.doc_id}
                className="flex items-center gap-2 rounded-xl bg-white border border-[#EBE5D9] px-3 py-2"
                data-testid={`vo-doc-${d.doc_id}`}
              >
                <span className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${meta.tint}`}>
                  <Icon size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[#2C2A29] truncate">{d.label || d.filename}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.tint}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#7A7571] truncate">
                    {d.uploaded_by_name || "ID firm"}
                    {" · "}
                    {new Date(d.uploaded_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    {d.size ? ` · ${fmtSize(d.size)}` : ""}
                  </div>
                </div>
                <a
                  href={`${api.defaults.baseURL}/vos/${voId}/documents/${d.doc_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost !p-1.5 text-[#7A7571] hover:text-[#2C2A29]"
                  title="Download"
                  data-testid={`vo-doc-${d.doc_id}-download`}
                >
                  <Download size={14} />
                </a>
                {canManage && (
                  <button
                    onClick={() => remove(d)}
                    className="btn-ghost !p-1.5 text-[#9F1239]"
                    title="Remove"
                    data-testid={`vo-doc-${d.doc_id}-delete`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </div>
  );
}

function UploadButton({ onClick, label, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#EBE5D9] bg-white text-[#2C2A29] hover:bg-[#F7F4EC] disabled:opacity-60"
      title={`Upload ${label}`}
    >
      {disabled ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} {label}
    </button>
  );
}
